import Modal from 'react-modal';
import React, { useMemo, useState } from 'react';
import turfBBox from '@turf/bbox';
import { polygon as turfPolygon } from '@turf/helpers';
import FileSaver from 'file-saver';
import { useAtom, useAtomValue } from 'jotai';
import { isDownloadControlerOpenAtom, mainMapAtom, mapClickEventAtom, printAttributionsAtom, selectedBaseMapAtom, selectedFeatureAtom } from '../../atoms';
import './ModalExportImage.css';
import { getCurrentMargin, margin2exportOptions } from '../../lib/cropUtiles';
import { attributionPosition } from '../../types/components/exportImage';
import { CameraOptions } from '../../types/components/exportImage';
import { AspectRatio, ContainerSize } from '../../types';
import { EXPORT_TYPE, NON_PADDING_DEFAULT_RATIO, STYLE_MODAL } from '../../constants/components/exportImage';
import SquareButton from '../button/SquareButton';
import { bbox2svg } from '../../lib/exportImage/exportImage';
import ExportControl from '../../lib/exportImage/exportControl';
import IconButton from '../button/IconButton';


const ModalExportImage: React.FC = () => {

  const [ exportFormat, setExportFormat ] = useState<'png' | 'svg'>('png');                             // 書き出し形式  
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const mainMap = useAtomValue(mainMapAtom);                                                            // mapオブジェクト
  const selectedBaseMap = useAtomValue(selectedBaseMapAtom);                                            // 選択された背景地図
  const selectedFeature = useAtomValue(selectedFeatureAtom);                                            // 選択されたフィーチャー
  const clickEvent = useAtomValue(mapClickEventAtom);                                                   // 選択されたフィーチャー
  const printAttributions = useAtomValue(printAttributionsAtom);
  const [isDownloadControlerOpen, setIsDownloadControlerOpen] = useAtom(isDownloadControlerOpenAtom);   // 画像書き出しモーダルの表示状態

  const hiddenSvg = useMemo(() => {                                                                     // SVGの非表示
    return selectedBaseMap?.id === 'smartcity-satellite';
  }, [selectedBaseMap]);

  const attributionOptions: { position: attributionPosition; area: number; } = {
    position: 'right-bottom',
    area: NON_PADDING_DEFAULT_RATIO,
  }


  /* *************
   *  モーダルを閉じる
   * *************/
  const handleCloseModal = () => {
    setIsDownloadControlerOpen(false);
    setExportFormat('png');
  }

  /* *************
   *  画像エクスポート
   * *************/
  const handleExport = async() => {
    if(!mainMap) { return; }

    const clientRect = mainMap.getContainer().getBoundingClientRect();
    const width = clientRect.width;
    const height = clientRect.height;
    const aspectRatio = [width??4, height??3] as AspectRatio;

    const fileName = `smartmap_yaizu_${new Date().toISOString().replace(/T/, '_').replace(/\..*/, '')}`;

    // 書き出し範囲を取得
    const dividedAspectRatioValue = aspectRatio[0] / aspectRatio[1];
    const nextBounds = margin2exportOptions(
      getCurrentMargin(mainMap, aspectRatio),
      mainMap,
      dividedAspectRatioValue
    );

    if(!nextBounds) { return; }

    setIsExporting(true);

    // クリック地点のフィーチャーを追加する
    if (clickEvent && !selectedFeature) {
      mainMap.addSource('featureGeojson', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [{
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [clickEvent?.lngLat.lng, clickEvent?.lngLat.lat]
            },
            properties: {}
          }],
        },
      } as maplibregl.GeoJSONSourceSpecification);

      mainMap.addLayer({
        id: 'featureGeojson-layer',
        type: 'circle',
        source: 'featureGeojson',
        paint: {
          'circle-radius': 10,
          'circle-color': '#ff0000', // 赤色
          'circle-stroke-width': 4,
          'circle-stroke-color': '#ffb2b2',
        },
      });
    }

    // ===== SVG形式で書き出す =====
    if(exportFormat === 'svg') {
      const { mapRect } = nextBounds;
      const bbox = turfBBox(turfPolygon([[...mapRect, mapRect[0]]]));
      const { svg, url } = await bbox2svg(mainMap, bbox);
      // svgファイルの書き出し
      FileSaver.saveAs(svg, `${fileName}.svg`);
      URL.revokeObjectURL(url);

    // ===== PNG形式で書き出す =====
    } else {

      const currentExportOptions: { containerSize: ContainerSize; cameraOptions: CameraOptions; } = {
        containerSize: {
          outerWidth: width,
          outerHeight: height,
          margin: { horizontal: 20, vertical: 20 }
        },
        cameraOptions: {
          center: mainMap.getCenter(),
          zoom: mainMap.getZoom(),
          pitch: mainMap.getPitch(),
          bearing: mainMap.getBearing()
        }
      }
      // Note：幅の問題で選択中のデータの1番目のみ出力
      const firstFeature = selectedFeature ? selectedFeature[Object.keys(selectedFeature)[0]][0] : undefined;
      
      // 画像の属性情報は削除
      if(firstFeature) {
        const imageRegex = /\.(jpeg|jpg|gif|png)$/i;
        const properties = firstFeature.properties;
        Object.keys(properties).forEach(key => {
          if (imageRegex.test(properties[key])) {
            delete properties[key];
          }
        });
      }
      
      const exportControl = new ExportControl( mainMap, currentExportOptions, firstFeature, printAttributions );
      
      await exportControl.handleExport(
        fileName,
        { width: width, height: height },
        {
          mode: 'png',
          download: true,
          thumbHeight: height,
          attributionOptions: attributionOptions,
        }
      );
      
    }

    // 画像出力が終わったら、クリック地点のフィーチャーを削除
    if (mainMap.getLayer('featureGeojson-layer')) {
      mainMap.removeLayer('featureGeojson-layer')
      mainMap.removeSource('featureGeojson');
    }

    setIsExporting(false);
    setIsDownloadControlerOpen(false);
  }

  return (
    <>
      <Modal
        isOpen={isDownloadControlerOpen}
        onRequestClose={handleCloseModal}
        style={STYLE_MODAL}
        appElement={document.getElementById('root')!}
      >
        <div className='modal-inner'>
          <div className='modal-header'>
            <h2 className='modal-title'>画像の書き出し</h2>
            <IconButton icon='close' onClick={handleCloseModal} />
          </div>

          <div className='modal-body'>
            { EXPORT_TYPE.map((value) => {
                return (
                  <label className="export-label" key={value.name}>
                    <input
                      type="radio"
                      value={ value.name }
                      onChange={() => {setExportFormat(value.name)}}
                      checked={exportFormat === value.name}
                      disabled={value.name === 'svg' && hiddenSvg}
                    />
                    <span>{ value.label }</span>
                  </label>
                )
            }) }
            <div className='form-footer'>
              <SquareButton label='書き出し' onClick={handleExport} bold leftSpace isLoading={isExporting} />
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ModalExportImage;
